import React from "react";
import { Box } from "@mui/material";
import { Chip, Typography, TypographyProps } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { getInstalledBaseTypeIcon } from "@dreebit-registry/vsm-microservice-components";
import {
    getParentPathNode,
    getPathNodesAbove,
    isNodeInTrash,
    InstalledBaseNodeWithPath,
} from "../../utils/installedBasePath";
import InstalledBasePathTooltip from "../InstalledBasePathTooltip";

interface Props {
    installedBaseNode?: InstalledBaseNodeWithPath | null;
    // "inherit" matches the DataGrid's plain-text cells (14px) instead of Joy's body-md.
    level?: TypographyProps["level"];
    // The asset table shows the trash badge behind the serial number instead.
    hideTrashBadge?: boolean;
}

// Table cell showing the direct parent installed base node (icon + title).
// Hovering reveals the full ancestor path; the tooltip stays open while
// hovered so the path can be selected and copied.
const InstalledBaseNodeParentCell = ({ installedBaseNode, level, hideTrashBadge = false }: Props) => {
    const { t } = useTranslation();
    const parent = getParentPathNode(installedBaseNode);
    // Trashed elements stay listed (e.g. in searchAssets) until purged — badge
    // them so the location column doesn't read like a regular installation.
    const inTrash = isNodeInTrash(installedBaseNode);

    if (!parent) return null;

    return (
        <InstalledBasePathTooltip pathNodes={getPathNodesAbove(installedBaseNode)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0 }}>
                {/* The raw <svg> is a flex item and would shrink with a truncated title. */}
                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                    {getInstalledBaseTypeIcon(parent.nodeType?.key ?? undefined, 18)}
                </Box>
                <Typography noWrap level={level}>{parent.title}</Typography>
                {inTrash && !hideTrashBadge && (
                    <Chip size="sm" color="warning" variant="soft">{t('In trash')}</Chip>
                )}
            </Box>
        </InstalledBasePathTooltip>
    );
};

export default InstalledBaseNodeParentCell;
